import { useState, useEffect } from 'react';
import { Copy, Check, Users, Crown, Trophy, Flame, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Referral {
  id: string;
  referee_id: string;
  status: string;
  signed_up_at: string;
  upgraded_at: string | null;
  legend_at: string | null;
  referee_name?: string;
}

interface ReferralReward {
  id: string;
  reward_type: string;
  days_awarded: number;
  created_at: string;
}

interface ReferralTabProps {
  userId: string;
  referralCode: string | null;
  isEvolverFounder?: boolean;
  compact?: boolean;
}

export function ReferralTab({ userId, referralCode, isEvolverFounder, compact }: ReferralTabProps) {
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [loading, setLoading] = useState(true);

  const referralLink = referralCode 
    ? `${window.location.origin}/?ref=${referralCode}`
    : null;

  useEffect(() => {
    fetchReferralData();
  }, [userId]);

  const fetchReferralData = async () => {
    try {
      // Fetch referrals made by this user
      const { data: referralsData, error: referralsError } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false });

      if (referralsError) throw referralsError;

      // Fetch referee names
      if (referralsData && referralsData.length > 0) {
        const refereeIds = referralsData.map(r => r.referee_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', refereeIds);

        const referralsWithNames = referralsData.map(r => ({
          ...r,
          referee_name: profiles?.find(p => p.id === r.referee_id)?.name || 'Anonymous'
        }));
        setReferrals(referralsWithNames);
      } else {
        setReferrals([]);
      }

      // Fetch rewards
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('referral_rewards')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (rewardsError) throw rewardsError;
      setRewards(rewardsData || []);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!referralLink) return;
    
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareLink = async () => {
    if (!referralLink) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on Evolve',
          text: 'Evolve with me — get 7 days free + 40% off your first month!',
          url: referralLink,
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      copyLink();
    }
  };

  const totalDaysEarned = rewards.reduce((sum, r) => sum + (r.days_awarded || 0), 0);
  const signupCount = referrals.filter(r => r.status === 'signed_up' || r.status === 'upgraded' || r.status === 'legend').length;
  const upgradeCount = referrals.filter(r => r.status === 'upgraded' || r.status === 'legend').length;
  const legendCount = referrals.filter(r => r.status === 'legend').length;

  return (
    <div className={`space-y-${compact ? '4' : '6'} ${compact ? '' : 'p-4'}`}>
      {/* Header */}
      {!compact && (
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">
            Evolve a Friend
          </h2>
          <p className="text-muted-foreground text-sm">
            Ascend Together
          </p>
        </div>
      )}

      {/* Evolver Founder Badge */}
      {isEvolverFounder && (
        <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-4 text-center">
          <div className="text-3xl mb-2">👑</div>
          <div className="font-bold text-purple-400">Evolver Founder</div>
          <div className="text-xs text-muted-foreground">You've helped a friend reach Legend status!</div>
        </div>
      )}

      {/* Referral Link Card */}
      <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Share2 className="h-4 w-4 text-primary" />
          Your Referral Link
        </div>
        
        {referralLink ? (
          <>
            <div className="bg-background/50 rounded-lg p-3 text-sm text-muted-foreground break-all font-mono">
              {referralLink}
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={copyLink}
                variant="outline"
                className="flex-1"
              >
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button 
                onClick={shareLink}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground">
            Loading your referral link...
          </div>
        )}
      </div>

      {/* Rewards Info */}
      <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-4 space-y-3">
        <div className="font-medium text-foreground">Rewards</div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Users className="h-4 w-4 text-blue-400" />
            <span>Friend signs up → <span className="text-blue-400 font-medium">+7 days Premium</span></span>
          </div>
          <div className="flex items-center gap-3 text-muted-foreground">
            <Crown className="h-4 w-4 text-yellow-400" />
            <span>Friend upgrades → <span className="text-yellow-400 font-medium">+30 days Premium</span></span>
          </div>
          <div className="flex items-center gap-3 text-muted-foreground">
            <Trophy className="h-4 w-4 text-purple-400" />
            <span>Friend hits Day 90 → <span className="text-purple-400 font-medium">Evolver Founder badge</span></span>
          </div>
        </div>
        <div className="pt-2 border-t border-border text-sm text-muted-foreground">
          <span className="text-primary">Friend benefits:</span> 7-day trial + 40% off first month
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-blue-400">{signupCount}</div>
          <div className="text-xs text-muted-foreground">Signups</div>
        </div>
        <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-yellow-400">{upgradeCount}</div>
          <div className="text-xs text-muted-foreground">Upgrades</div>
        </div>
        <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-purple-400">{legendCount}</div>
          <div className="text-xs text-muted-foreground">Legends</div>
        </div>
      </div>

      {/* Total Days Earned */}
      {totalDaysEarned > 0 && (
        <div className="bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Flame className="h-5 w-5 text-primary" />
            <span className="text-lg font-bold text-foreground">{totalDaysEarned} days</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Premium time earned from referrals</div>
        </div>
      )}

      {/* Referral List */}
      {referrals.length > 0 && (
        <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-4 space-y-3">
          <div className="font-medium text-foreground">Your Referrals</div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {referrals.map((referral) => (
              <div 
                key={referral.id}
                className="flex items-center justify-between bg-background/30 rounded-lg p-3"
              >
                <div>
                  <div className="font-medium text-sm text-foreground">
                    {referral.referee_name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(referral.signed_up_at).toLocaleDateString()}
                  </div>
                </div>
                <div className={`text-xs px-2 py-1 rounded-full ${
                  referral.status === 'legend' 
                    ? 'bg-purple-500/20 text-purple-400'
                    : referral.status === 'upgraded'
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {referral.status === 'legend' ? '🏆 Legend' : 
                   referral.status === 'upgraded' ? '👑 Premium' : '✓ Signed Up'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA Footer */}
      <div className="text-center text-xs text-muted-foreground pt-4">
        Share your link — evolve a friend, ascend together.<br />
        <span className="text-primary">Top referrers get Immortal shoutouts.</span>
      </div>
    </div>
  );
}
